'use client'

import type { EventProperties } from '@segment/analytics-next'
import AnalyticsConstants from '../../constants/analytics'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

// GA4 event names: lowercase letters/numbers/underscores, must start with a letter, 40 char max.
// https://support.google.com/analytics/answer/13316687
const GA4_EVENT_NAME_MAX_LENGTH = 40

export function toGa4EventName(rawName: string): string {
  const withoutTaxonomyTags = rawName.replace(/\([^)]*\)/g, '')
  const normalized = withoutTaxonomyTags
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, GA4_EVENT_NAME_MAX_LENGTH)
    .replace(/_+$/g, '')

  return /^[a-z]/.test(normalized) ? normalized : `event_${normalized}`
}

// Segment event string -> GA4 recommended event name. Only map an event here when
// the GA4 standard event's own definition genuinely matches what it represents -
// a bad mapping corrupts GA4's built-in reports (e.g. Lead generation).
// https://support.google.com/analytics/answer/9267735
const GA4_STANDARD_EVENT_MAP: Record<string, string> = {}

// INIT (page view) events are represented in GA4 exclusively via the dedicated
// `page_view` event fired from useViewPage - excluded here so a page view doesn't
// also fire as a duplicate custom event under its own name.
const GA4_PAGE_VIEW_EVENTS = new Set<string>(Object.values(AnalyticsConstants.EVENTS.INIT))

// Explicit allowlist: only these property keys are ever forwarded to GA4, and
// none are PII today. Read the audit process in
// docs/decisions/ga4-property-allowlist.md before adding a key here.
const GA4_ALLOWED_PROPERTY_KEYS = new Set<string>(['cta'])

function sanitizeForGa4(properties?: EventProperties): Record<string, unknown> {
  if (!properties) return {}
  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => GA4_ALLOWED_PROPERTY_KEYS.has(key))
  )
}

function gtag(...args: unknown[]): void {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args)
  }
}

export function isGa4PageViewEvent(eventName: string): boolean {
  return GA4_PAGE_VIEW_EVENTS.has(eventName)
}

export function sendGa4Event(rawEventName: string, properties?: EventProperties): void {
  const ga4EventName = GA4_STANDARD_EVENT_MAP[rawEventName] ?? toGa4EventName(rawEventName)

  gtag('event', ga4EventName, {
    ...sanitizeForGa4(properties),
    source_event: rawEventName,
  })
}

export function sendGa4PageView(pagePath: string): void {
  const queryString = typeof window !== 'undefined' ? window.location.search : ''
  const fullPath = `${pagePath}${queryString}`

  gtag('event', 'page_view', {
    page_location:
      typeof window !== 'undefined' ? `${window.location.origin}${fullPath}` : undefined,
    page_path: fullPath,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
  })
}
